import { FormControl, IconButton, Select, SelectOption, Stack, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { Draft, produce } from 'immer';
import { Plus as PlusIcon } from 'mdi-material-ui';
import { useEffect, useRef, useState } from 'react';
import { GenericOverrides, OVERRIDES_EDITOR_WIDTH, Override, OverrideMatcherType } from '@/core';
import { OptionsEditorGroup } from '@/plugin-editor-components';
import { useFormattedSeriesNames } from '../../../utils';
import { EmptyState } from './EmptyState';
import { OverrideInput } from './OverrideInput';
import { BaseOverrideSchema, OverrideOptions } from './types';

export type OverridesEditorProps<T extends BaseOverrideSchema> = {
  overrides?: GenericOverrides<T>;
  onChange: (overrides?: GenericOverrides<T>) => void;
  overrideOptions: OverrideOptions<T>;
};

const OVERRIDE_OPTIONS: SelectOption[] = [
  { label: 'Series name', key: 'ByName' },
  { label: 'Series name with matching regex', key: 'ByRegex' },
];

export function OverridesEditor<T extends BaseOverrideSchema>(props: OverridesEditorProps<T>) {
  const { overrides, onChange, overrideOptions } = props;

  // Get all formatted series names for use in autocomplete matches.
  const seriesNames = useFormattedSeriesNames();

  // Every time a new override is added, we want to focus the recently added input
  const recentlyAddedInputRef = useRef<HTMLInputElement | null>(null);
  const focusRef = useRef(false);
  useEffect(() => {
    if (!recentlyAddedInputRef.current || !focusRef.current) return;
    recentlyAddedInputRef.current?.focus();
    focusRef.current = false;
  }, [overrides?.length]);

  const [isSelectMatcherOpen, setIsSelectMatcherOpen] = useState(false);

  const [selectedOverrideMatcher, setSelectedOverrideMatcher] = useState<SelectOption>();

  // Add new input after any existing overrides and focus the field
  const addOverrideInput = (overrideMatcher: OverrideMatcherType): void => {
    const override: Override<T> = {
      value: '',
      properties: {} as T,
      matcher: {
        type: overrideMatcher,
      },
    };
    focusRef.current = true;
    onChange(
      produce(overrides, (draft) => {
        const overrides = draft;
        if (overrides) {
          overrides.push(override as Draft<Override<T>>);
        }
      })
    );
    setSelectedOverrideMatcher(undefined);
  };

  const deleteOverrideInput = (index: number) => {
    if (overrides !== undefined) {
      const newOverrides = produce(overrides, (draft) => {
        draft.splice(index, 1);
      });
      onChange(newOverrides.length ? newOverrides : undefined);
    }
  };

  return (
    <OptionsEditorGroup
      title="Override"
      icon={
        <Tooltip Content="Add series override">
          <IconButton size="small" aria-label="Add series override" onClick={() => setIsSelectMatcherOpen(true)}>
            <PlusIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      {(overrides === undefined || overrides.length === 0) && !isSelectMatcherOpen ? (
        <Stack direction="column" alignItems="center">
          <EmptyState />
          <Typography>
            Use overrides to customize your visualization. <br /> Click the plus button to add an override.
          </Typography>
        </Stack>
      ) : null}
      <FormControl
        sx={{
          '> *': {
            borderBottom: 1,
            borderColor: (theme) =>
              theme.featureFlags.panwRebrandStubYear ? theme.palette.border.default : 'grey.300',
            marginBottom: 1.25,
            '&:last-child': {
              border: 'none',
            },
          },
        }}
      >
        {overrides?.map((override, index) => (
          <OverrideInput
            key={`override-item-${override.value}-${index}`}
            override={override}
            overrideOptions={overrideOptions}
            onOverrideChange={(newOverride) => {
              onChange(
                produce(overrides, (draft) => {
                  if (draft) {
                    draft[index] = newOverride as Draft<Override<T>>;
                  }
                })
              );
            }}
            onDelete={() => deleteOverrideInput(index)}
            inputRef={index === overrides.length - 1 ? recentlyAddedInputRef : undefined}
            seriesNames={seriesNames}
          />
        ))}
        {isSelectMatcherOpen && (
          <Select
            Label="Select override option"
            disableClearable
            options={OVERRIDE_OPTIONS}
            value={selectedOverrideMatcher}
            onChange={(_, value) => {
              if (value) {
                addOverrideInput(value.key as OverrideMatcherType);
                setIsSelectMatcherOpen(false);
              }
            }}
            open={isSelectMatcherOpen}
            onClose={() => {
              setIsSelectMatcherOpen(false);
            }}
            size="medium"
            sx={{ minWidth: OVERRIDES_EDITOR_WIDTH }}
          />
        )}
      </FormControl>
    </OptionsEditorGroup>
  );
}
